import React, { useState, ChangeEvent, useEffect } from 'react';
import { logger } from '../services/logger';
import { Button } from '../components/ui/Button/Button';
import { Card } from '../components/ui/Card/Card';
import { Input } from '../components/ui/Input/Input';
import { useThemeContext } from '../context/ThemeContext';
import { cn } from '@/utils/classNames';

// Demo steps for the page
const demoSteps = [
  { id: '1', name: 'Extract Headers', active: true },
  { id: '2', name: 'Analyze Sender', active: true },
  { id: '3', name: 'Check Links', active: true },
  { id: '4', name: 'Analyze Content', active: false },
  { id: '5', name: 'Generate Summary', active: true },
];

const StepsPage: React.FC = () => {
  const theme = useThemeContext();
  const [newStepName, setNewStepName] = useState('');
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [steps, setSteps] = useState(demoSteps);
  
  const handleAddStep = () => {
    if (!newStepName.trim()) return;
    
    setIsAddingStep(true);
    logger.info('Adding new step', { message: `New step: ${newStepName}` });
    
    // Simulate network request
    setTimeout(() => {
      const newId = (steps.length + 1).toString();
      setSteps([...steps, { id: newId, name: newStepName, active: true }]);
      setNewStepName('');
      setIsAddingStep(false);
    }, 800);
  };
  
  // Function to toggle step active status
  const toggleStepActive = (id: string) => {
    setSteps(steps.map(step => 
      step.id === id ? { ...step, active: !step.active } : step
    ));
  };
  
  // Function to delete a step
  const deleteStep = (id: string) => {
    setSteps(steps.filter(step => step.id !== id));
  };
  
  // Animation delay for staggered appearance
  const getAnimationDelay = (index: number) => {
    return `${100 + (index * 50)}ms`;
  };
  
  return (
    <div className="container mx-auto px-4 py-8 space-y-8 animate-fadeIn">
      <div className="text-center mb-8 space-y-2">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Analysis Steps Manager
        </h1>
        
        <p className="text-text-secondary dark:text-text-secondary max-w-2xl mx-auto">
          Customize the steps used in your phishing email analysis workflow. 
          Arrange steps in order of execution and toggle which ones to include.
        </p>
      </div>
      
      <div className="mb-8 animate-fadeIn" style={{ animationDelay: '100ms' }}>
        <Card variant="glass" className="p-6 backdrop-blur-md">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-grow">
              <Input
                label="New Step Name"
                value={newStepName}
                onChange={(e) => setNewStepName(e.target.value)}
                placeholder="Enter step name..."
                variant="glass"
                isGlowing
              />
            </div>
            <Button 
              onClick={handleAddStep}
              variant="accent"
              size="md"
              className="whitespace-nowrap"
              isLoading={isAddingStep}
              hasRipple
              isGlowing
            >
              Add Step
            </Button>
          </div>
        </Card>
      </div>
      
      <div className="grid gap-4">
        {steps.map((step, index) => (
          <div 
            key={step.id} 
            className="animate-fadeIn" 
            style={{ animationDelay: getAnimationDelay(index) }}
          >
            <Card 
              variant="glass"
              hasHoverEffect
              className={cn(
                "transition-all duration-300",
                !step.active && "opacity-70 hover:opacity-90"
              )}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className={cn(
                    "inline-block w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                    step.active 
                      ? 'bg-gradient-to-r from-primary to-accent text-white shadow-glow'
                      : 'bg-surface-secondary text-text-secondary'
                  )}>
                    {step.id}
                  </span>
                  <span className="font-medium text-text-primary dark:text-text-primary text-lg">
                    {step.name}
                  </span>
                </div>
                
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => logger.info('Edit step clicked', { message: `Edit step: ${step.id}` })}
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    }
                    hasRipple
                  >
                    Edit
                  </Button>
                  <Button
                    variant={step.active ? "secondary" : "primary"}
                    size="sm"
                    onClick={() => toggleStepActive(step.id)}
                    icon={step.active ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    hasRipple
                  >
                    {step.active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteStep(step.id)}
                    className="text-error hover:bg-error hover:text-white border-error"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    }
                    hasRipple
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        ))}
        
        {steps.length === 0 && (
          <Card variant="glass" className="p-6 text-center animate-fadeIn" style={{ animationDelay: '300ms' }}>
            <p className="text-text-secondary dark:text-text-secondary">
              No steps defined yet. Add your first step using the form above.
            </p>
          </Card>
        )}
      </div>
      
      {steps.length > 0 && (
        <div className="mt-8 text-center animate-fadeIn" style={{ animationDelay: '600ms' }}>
          <Button 
            variant="primary" 
            size="lg"
            isGlowing
            hasRipple
            onClick={() => logger.info('Save workflow clicked')}
            className="group"
          >
            <span className="inline-block group-hover:scale-105 transition-transform">
              Save Workflow
            </span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default StepsPage; 